import { useEffect, useMemo, useState } from "react"
import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { useParams, useNavigate, Link } from "react-router-dom"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import {
  Calendar, Clock, Users, Link as LinkIcon, Copy, Video,
  Edit, Trash2, Share2, ExternalLink, Repeat, FileText,
  ChevronDown, ChevronUp, Globe, ShieldCheck, Lock, Eye, EyeOff,
  Check, Bell, Mic, Camera, Monitor, MessageSquare, Smile,
  UserCheck, Radio, Wifi, Phone, Paperclip, CheckCircle2,
} from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Badge } from "@/clientWeb/components/ui/badge"
import { useMeetingsStore, selectMeetingById } from "./store/meetings.store"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { meetingsService, formatMeetingTime, isPastMeeting, meetingTimesInZones } from "./services/meetings.service"
import { formatTimezoneLabel, canonicalTimezone } from "@/shared/utils"
import { meetingsApi } from "./services/meetings.api"
import type { MeetingSettings } from "./models/meeting.model"
import { deleteMeetingCopy, describeRecurrence, joinLockedUntil, joinPathId } from "./utils/recurrence"
import { displayedMeetingTimes } from "./utils/actual-duration"
import { useSeriesScopeActions } from "./hooks/useSeriesScopeActions"
import { useMarkPast } from "./hooks/useMarkPast"
import { JoinLockHint } from "./components/JoinLockHint"
import { useHostMeetingPassword } from "./hooks/useHostMeetingPassword"

function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = [
    "bg-primary/20 text-primary",
    "bg-violet-500/20 text-violet-600",
    "bg-emerald-500/20 text-emerald-600",
    "bg-amber-500/20 text-amber-600",
    "bg-pink-500/20 text-pink-600",
  ]
  const ci = (name.charCodeAt(0) || 0) % colors.length
  return (
    <div
      className={`rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${colors[ci]}`}
      style={{ width: size, height: size }}
    >
      {initials}
    </div>
  )
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-3 border-b border-border/50 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 sm:w-44 shrink-0 pt-0.5">{label}</span>
      <div className="flex-1 text-sm text-foreground">{children}</div>
    </div>
  )
}

function ToggleBadge({ active, label, Icon }: { active: boolean; label: string; Icon: React.ElementType }) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${
        active
          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20"
          : "bg-muted/40 text-muted-foreground border-border"
      }`}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
      {active && <Check className="w-3 h-3" />}
    </div>
  )
}

export default function MeetingDetailsPage() {
  const { t, language } = useLanguage()
  const { id } = useParams()
  const navigate = useNavigate()

  const meeting = useMeetingsStore(selectMeetingById(id))
  const deleteMeeting = useMeetingsStore((s) => s.deleteMeeting)
  const { markPast: handleMarkPast, markingPast, dialog: markPastDialog } = useMarkPast(meeting?.id)
  const authUser = useAuthStore((s) => s.user)

  const [copied, setCopied] = useState(false)
  const [showAllInvitees, setShowAllInvitees] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordCopied, setPasswordCopied] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [shareDialogCopied, setShareDialogCopied] = useState(false)
  const seriesActions = useSeriesScopeActions(meeting, {
    onDeleteOneOff: () => setShowDeleteConfirm(true),
    onDeleted: () => navigate("/meetings"),
  })

  // A recurring meeting is added to a calendar as the whole series: the same
  // file its emails carry, and its repeat rule for the Google link.
  const [seriesCalendar, setSeriesCalendar] = useState<{ ics: string; rrule: string } | null>(null)
  const isSeries = !!meeting?.series
  const meetingIdForCalendar = meeting?.id
  useEffect(() => {
    setSeriesCalendar(null)
    if (!isSeries || !meetingIdForCalendar) return
    let cancelled = false
    meetingsApi
      .calendar(meetingIdForCalendar)
      .then((c) => { if (!cancelled) setSeriesCalendar(c) })
      .catch(() => { /* falls back to this one day */ })
    return () => { cancelled = true }
  }, [isSeries, meetingIdForCalendar])

  const isHost = useMemo(() => {
    if (!meeting || !authUser) return false
    if (meeting.hostId && authUser.id) return meeting.hostId === authUser.id
    if (meeting.hostEmail && authUser.email) return meeting.hostEmail === authUser.email
    return false
  }, [meeting, authUser])

  /**
   * Who may open the edit screen — the host, or a co-host the server recognises.
   *
   * Kept apart from `isHost`, which still guards the join keys and the password:
   * those are the owner's to hold, and a co-host promoted for one meeting is not
   * owed the credential that outlives it.
   */
  const canModerate = useMemo(
    () => meeting?.canModerate ?? isHost,
    [meeting, isHost],
  )

  const serverPassword = useHostMeetingPassword(meeting?.id, !!meeting?.settings?.passwordEnabled && isHost)
  const meetingPassword = useMemo(() => {
    if (!meeting?.settings?.passwordEnabled || !isHost) return undefined
    return serverPassword ?? meetingsService.getPasswordForHost(meeting.id, authUser?.id)
  }, [meeting, isHost, authUser?.id, serverPassword])

  if (!meeting) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center p-8 text-center">
        <h2 className="text-base font-semibold text-foreground mb-1">{t("meetings.notFound")}</h2>
        <p className="text-sm text-muted-foreground mb-4">{t("meetings.notFoundDesc")}</p>
        <Button onClick={() => navigate("/meetings")}>{t("meetings.backToMeetings")}</Button>
      </div>
    )
  }

  const settings: MeetingSettings = meeting.settings
  const startTime = new Date(meeting.startTimeMs)
  const endTime = new Date(meeting.endTimeMs)
  // What the Time row shows: booked start, real end if it overran, minutes
  // actually spent. `startTime`/`endTime` stay the booking for the calendar export.
  const shownTimes = displayedMeetingTimes(meeting)
  // Shared with the dashboard so the two screens cannot disagree about what
  // "past" means — this used to be end-time only, which missed a meeting the
  // server had already marked completed.
  const isPast = isPastMeeting(meeting)
  const deleteCopy = deleteMeetingCopy(meeting, t, language)
  const isLive = meeting.status === "ongoing"
  // Inside its scheduled window: started, not yet due to finish. The only time
  // there is a running meeting to close off.
  const isInProgress = !isPast && startTime <= new Date()

  const meetingUrl = `${window.location.origin}/meeting/${meeting.meetingId ?? meeting.id}`

  const formatDate = (d: Date) =>
    d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
  const formatTime = (d: Date) => formatMeetingTime(d.getTime())
  // The meeting's own schedule always shows; "your time" only when it reads
  // differently from the viewer's own zone. Keeps this screen and the
  // dashboard panel from disagreeing about what to show.
  const zoneTimes = meetingTimesInZones(shownTimes.startMs, shownTimes.endMs, meeting.timezone)
  const timezoneLabel = formatTimezoneLabel(canonicalTimezone(meeting.timezone))

  const copyUrl = () => {
    navigator.clipboard.writeText(meetingUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success({ title: t("common.copied"), description: t("meetings.meetingLinkCopied") })
    })
  }

  const copyPassword = () => {
    if (!meetingPassword) return
    navigator.clipboard.writeText(meetingPassword).then(() => {
      setPasswordCopied(true)
      setTimeout(() => setPasswordCopied(false), 1600)
      toast.success({ title: t("meetings.passwordCopied"), description: t("meetings.passwordCopiedDesc") })
    })
  }

  const toICSDate = (d: Date) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z"

  const downloadICS = () => {
    const ics = seriesCalendar?.ics ?? [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//InMeet//EN",
      "BEGIN:VEVENT",
      `DTSTART:${toICSDate(startTime)}`,
      `DTEND:${toICSDate(endTime)}`,
      `SUMMARY:${meeting.title}`,
      `DESCRIPTION:${(meeting.description ?? "").replace(/\n/g, "\\n")}`,
      `LOCATION:${meetingUrl}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n")
    const blob = new Blob([ics], { type: "text/calendar" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${meeting.title.replace(/\s+/g, "_")}.ics`
    a.click()
    toast.success({ title: t("meetings.calendarFileSaved"), description: `${meeting.title}.ics ${t("meetings.downloadedToDevice")}` })
    URL.revokeObjectURL(url)
  }

  const googleCalendarUrl = () => {
    // A series goes in from its first meeting, repeating by its rule.
    const first = seriesCalendar && meeting.series ? new Date(meeting.series.startsAtMs) : startTime
    const firstEnd = new Date(first.getTime() + (endTime.getTime() - startTime.getTime()))
    const p = new URLSearchParams({
      action: "TEMPLATE",
      text: meeting.title,
      dates: `${toICSDate(first)}/${toICSDate(firstEnd)}`,
      details: `${meeting.description ?? ""}\n\nJoin: ${meetingUrl}`,
      location: meetingUrl,
      ...(seriesCalendar && meeting.series
        ? { recur: `RRULE:${seriesCalendar.rrule}`, ctz: meeting.series.timezone }
        : {}),
    })
    return `https://calendar.google.com/calendar/render?${p.toString()}`
  }

  const outlookCalendarUrl = () => {
    const p = new URLSearchParams({
      path: "/calendar/action/compose",
      rru: "addevent",
      subject: meeting.title,
      startdt: startTime.toISOString(),
      enddt: endTime.toISOString(),
      body: `${meeting.description ?? ""}\n\nJoin: ${meetingUrl}`,
      location: meetingUrl,
    })
    return `https://outlook.live.com/calendar/0/deeplink/compose?${p.toString()}`
  }

  const invitees = meeting.invitees ?? []
  const visibleInvitees = showAllInvitees ? invitees : invitees.slice(0, 5)
  const attachments = meeting.attachments ?? []

  return (
    <div className="flex flex-col flex-1 overflow-y-auto px-4 py-4 sm:px-6 max-w-3xl mx-auto w-full">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <Link to="/meetings" className="hover:text-foreground transition-colors">{t("meetings.title")}</Link>
        <span>/</span>
        <span className="text-foreground">{t("meetings.meetingDetails")}</span>
      </div>

      {/* Header card */}
      <div className="bg-card rounded-2xl border border-border p-5 mb-4">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {isLive && (
                <Badge className="bg-red-500/10 text-red-500 border-red-500/20 font-semibold text-[11px] gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  {t("meetings.live")}
                </Badge>
              )}
              {meeting.recurring && (
                <Badge variant="secondary" className="text-[11px] gap-1">
                  <Repeat className="w-3 h-3" />
                  {t("meetings.schedule.recurring")}
                </Badge>
              )}
              {isPast && (
                <Badge variant="secondary" className="text-[11px]">{t("meetings.past")}</Badge>
              )}
              {settings.passwordEnabled && (
                <Badge variant="secondary" className="text-[11px] gap-1">
                  <Lock className="w-3 h-3" />
                  {t("meetings.passwordProtected") || "Password protected"}
                </Badge>
              )}
            </div>
            <h1 className="text-xl font-bold text-foreground mb-1">{meeting.title}</h1>
            {meeting.description && (
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line break-words">{meeting.description}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {!isPast && canModerate && (
              <>
                <Button variant="ghost" size="icon" onClick={seriesActions.edit} title="Edit">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={seriesActions.remove}
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" title="Share" onClick={() => setShowShareDialog(true)}>
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Join / Past button */}
        {isPast ? (
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => navigate(`/meetings/${meeting.id}/past`)}
          >
            {t("meetings.viewPastMeeting")}
          </Button>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {/* A later day of a series cannot be joined yet: say when, rather than refuse on click. */}
            <JoinLockHint lockedUntilMs={joinLockedUntil(meeting)}>
              <Button
                className="w-full sm:w-auto"
                disabled={joinLockedUntil(meeting) !== null}
                onClick={() => navigate(`/meeting/${joinPathId(meeting)}`)}
              >
                <Video className="w-4 h-4 mr-2" />
                {t("meetings.joinMeeting")}
              </Button>
            </JoinLockHint>

            {/* Only the host, and only while the meeting is actually running —
                there is nothing to close off before it starts, and the server
                refuses it outside that window anyway. */}
            {isInProgress && isHost && (
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                disabled={markingPast}
                onClick={handleMarkPast}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {markingPast ? t("meetings.markingAsPast") : t("meetings.markAsPast")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Details card */}
      <div className="bg-card rounded-2xl border border-border px-5 py-2 mb-4">
        <InfoRow label={t("meetings.dateLabel") || "Date"}>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
            <span>{formatDate(startTime)}</span>
          </div>
        </InfoRow>

        <InfoRow label={t("meetings.timeLabel") || "Time"}>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
              <span>
                {zoneTimes.meeting
                  ? `${t("meetings.meetingTime")}: ${zoneTimes.meeting.range} ${zoneTimes.meeting.zoneLabel}`
                  : `${formatTime(new Date(shownTimes.startMs))} - ${formatTime(new Date(shownTimes.endMs))}`}
                {" "}
                <span className="text-muted-foreground">({shownTimes.minutes} {t("common.minutes")})</span>
              </span>
            </div>
            {zoneTimes.viewer && (
              <div className="flex items-center gap-2 pl-6 text-muted-foreground text-xs">
                <span>{t("meetings.yourTime")}: {zoneTimes.viewer.range} {zoneTimes.viewer.zoneLabel}</span>
              </div>
            )}
          </div>
        </InfoRow>

        <InfoRow label={t("webinar.timeZone") || "Timezone"}>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
            <span>{timezoneLabel}</span>
          </div>
        </InfoRow>

        {meeting.recurring && meeting.recurringConfig && (
          <InfoRow label={t("meetings.recurrenceLabel") || "Recurrence"}>
            <div className="flex items-start gap-2">
              <Repeat className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              {/* A series counts from its first meeting, not from this one. */}
              <p className="min-w-0 break-words">
                {describeRecurrence(
                  meeting.series?.rule ?? meeting.recurringConfig,
                  new Date(meeting.series?.startsAtMs ?? meeting.startTimeMs),
                  language,
                  t,
                )}
              </p>
            </div>
          </InfoRow>
        )}

        <InfoRow label={t("meetings.hostLabel") || "Host"}>
          <div className="flex items-center gap-2">
            <Avatar name={meeting.host} size={24} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{meeting.host}</p>
              {meeting.hostEmail && <p className="text-xs text-muted-foreground truncate">{meeting.hostEmail}</p>}
            </div>
          </div>
        </InfoRow>

        <InfoRow label={t("meetings.seatsLabel")}>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="font-medium">{meeting.seats ?? 2}</span>
          </div>
        </InfoRow>

        <InfoRow label={t("meetings.meetingIdLabel")}>
          <code className="bg-muted/60 px-2 py-0.5 rounded text-primary font-mono text-xs">{meeting.meetingId}</code>
        </InfoRow>

        {/* The meeting's own code, what the dashboard's Screen Share asks for.
            Shown whatever the clock says: an overrunning meeting can still be
            shared into, and the share's own check names an ended one. */}
        {meeting.meetingId && (
          <InfoRow label={t("meetings.screenShareId")}>
            <code className="bg-muted/60 px-2 py-0.5 rounded text-primary font-mono text-xs">{meeting.meetingId}</code>
          </InfoRow>
        )}

        <InfoRow label={t("meetings.meetingUrlLabel")}>
          <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2 flex-wrap">
            <LinkIcon className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="flex-1 text-xs font-mono text-muted-foreground truncate min-w-0">{meetingUrl}</span>
            <button
              onClick={copyUrl}
              className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors font-medium shrink-0"
              title={t("common.copy")}
            >
              {copied ? <><Check className="w-3.5 h-3.5" />{t("meetings.copied")}</> : <><Copy className="w-3.5 h-3.5" />{t("common.copy")}</>}
            </button>
            <button
              onClick={() => setShowShareDialog(true)}
              className="text-muted-foreground hover:text-primary transition-colors shrink-0"
              title={t("common.share")}
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
            <a
              href={meetingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
              title="Open"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </InfoRow>

        <InfoRow label={t("meetings.inviteesLabel")}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground">
                {invitees.length === 0
                  ? t("meetings.noInvitees")
                  : t("meetings.inviteesCount", { n: invitees.length })}
              </span>
            </div>
            {invitees.length > 0 && (
              <div className="flex flex-col gap-1.5">
                {visibleInvitees.map((email, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Avatar name={email} size={28} />
                    <div className="min-w-0">
                      <p className="text-sm text-foreground truncate">{email}</p>
                    </div>
                  </div>
                ))}
                {invitees.length > 5 && (
                  <button
                    onClick={() => setShowAllInvitees(!showAllInvitees)}
                    className="flex items-center gap-1 text-xs text-primary font-medium mt-1 w-fit"
                  >
                    {showAllInvitees ? (
                      <><ChevronUp className="w-3.5 h-3.5" />{t("meetings.showLess")}</>
                    ) : (
                      <><ChevronDown className="w-3.5 h-3.5" />+{invitees.length - 5} {t("common.more")}</>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </InfoRow>
      </div>

      {/* Security & Password */}
      {settings.passwordEnabled && (
        <div className="bg-card rounded-2xl border border-border p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">{t("meetings.securityAdvanced") || "Security"}</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Lock className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{t("meetings.meetingPassword") || "Meeting password"}</span>
            </div>
            {isHost && meetingPassword ? (
              <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-lg px-3 py-2">
                <code className="flex-1 text-sm font-mono tracking-widest text-foreground truncate">
                  {showPassword ? meetingPassword : "•".repeat(meetingPassword.length)}
                </code>
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-muted-foreground hover:text-primary transition-colors p-1"
                  title={showPassword ? t("meetings.hidePassword") : t("meetings.showPassword")}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={copyPassword}
                  className="text-muted-foreground hover:text-primary transition-colors p-1"
                  title={passwordCopied ? t("common.copied") : t("meetings.copyPassword")}
                >
                  {passwordCopied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("meetings.participantsPrompted") || "Participants will be prompted for the password at join time."}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Meeting Options / Settings */}
      {/* Only the people who can change these settings need to see them, and a
          past meeting has nothing left here to configure. */}
      {!isPast && canModerate && (
        <div className="bg-card rounded-2xl border border-border p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">{t("meetings.meetingOptions")}</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            <ToggleBadge active={settings.muteOnEntry} label={t("meetings.muteOnEntry")} Icon={Mic} />
            <ToggleBadge active={settings.videoOnEntry} label={t("meetings.videoOnEntry")} Icon={Camera} />
            <ToggleBadge active={settings.waitingRoomEnabled} label={t("meetings.waitingRoom")} Icon={UserCheck} />
            <ToggleBadge active={settings.lockAfterStart} label={t("meetings.lockAfterStart")} Icon={Lock} />
            <ToggleBadge active={settings.autoRecord} label={t("meetings.autoRecord")} Icon={Radio} />
            <ToggleBadge active={settings.transcriptionEnabled} label={t("meetings.transcription")} Icon={FileText} />
            <ToggleBadge active={settings.allowJoinAnytime} label={t("meetings.anyTimeJoin")} Icon={Clock} />
            <ToggleBadge active={settings.allowChat} label={t("meetings.allowChat")} Icon={MessageSquare} />
            <ToggleBadge active={settings.allowReactions} label={t("meetings.allowReactions")} Icon={Smile} />
            <ToggleBadge active={settings.allowScreenShare !== "host_only"} label={t("meetings.screenShare")} Icon={Monitor} />
            <ToggleBadge active={settings.simulcast} label={t("meetings.simulcast")} Icon={Wifi} />
            <ToggleBadge active={settings.sipEnabled} label={t("meetings.sip")} Icon={Phone} />
            <ToggleBadge active={settings.telepresence} label={t("meetings.telepresence")} Icon={Video} />
          </div>
        </div>
      )}

      {/* Reminders */}
      {settings.reminderEnabled && (
        <div className="bg-card rounded-2xl border border-border p-5 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">{t("meetings.reminderLabel") || "Reminder"}</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("meetings.reminderBefore", { n: settings.reminderBeforeMinutes }) ||
              `Reminder ${settings.reminderBeforeMinutes} minutes before start`}
          </p>
        </div>
      )}

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="bg-card rounded-2xl border border-border p-5 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Paperclip className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              {t("meetings.attachments") || "Attachments"} ({attachments.length})
            </h3>
          </div>
          <div className="divide-y divide-border/40">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-3 py-2">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{(a.size / 1024).toFixed(1)} KB · {a.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add to Calendar */}
      <div className="bg-card rounded-2xl border border-border p-5 mb-4">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          {t("meetings.addToCalendar")}
        </h3>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline" size="sm" className="gap-2"
            onClick={() => window.open(googleCalendarUrl(), "_blank")}
          >
            <img src="https://www.gstatic.com/images/branding/product/1x/calendar_16dp.png" alt="Google" className="w-4 h-4" />
            {t("meetings.googleCalendar")}
          </Button>
          <Button
            variant="outline" size="sm" className="gap-2"
            onClick={() => window.open(outlookCalendarUrl(), "_blank")}
          >
            <img src="https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg" alt="Outlook" className="w-4 h-4" />
            {t("meetings.outlook")}
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={downloadICS}>
            <FileText className="w-4 h-4" />
            {t("meetings.downloadIcs")}
          </Button>
        </div>
      </div>

      {/* Delete confirm */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl border border-border p-6 max-w-sm w-full shadow-2xl">
            {/* One occurrence of a series cancels that day only (v1 has no
                "whole series" delete), so the dialog names the day. */}
            <h3 className="text-base font-semibold text-foreground mb-2">
              {deleteCopy.title}
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              {deleteCopy.warning}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>{t("common.cancel")}</Button>
              <Button variant="destructive" onClick={() => void (async () => {
                setShowDeleteConfirm(false)
                try {
                  // Server first — see the store's `deleteMeeting`. The local
                  // -only remove this replaced hid the meeting until the next
                  // fetch and deleted nothing.
                  await deleteMeeting(meeting.id)
                  toast.success({ title: deleteCopy.doneTitle, description: deleteCopy.doneDescription })
                  navigate("/meetings")
                } catch {
                  toast.error({ title: t("meetings.couldNotDeleteMeeting") })
                }
              })()}>
                {deleteCopy.confirm}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* This occurrence / All occurrences, for a day of a series */}
      {seriesActions.dialog}
      {/* Mark as Past while people are still in the room */}
      {markPastDialog}

      {/* Share dialog */}
      {showShareDialog && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowShareDialog(false)}
        >
          <div
            className="bg-card rounded-2xl border border-border p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-foreground mb-1 flex items-center gap-2">
              <Share2 className="w-4 h-4 text-primary" />
              {t("meetings.shareMeeting")}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">{t("meetings.shareMeetingDesc")}</p>
            <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-lg px-3 py-2 mb-4">
              <LinkIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="flex-1 text-xs font-mono text-foreground truncate select-all">{meetingUrl}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(meetingUrl)
                  setShareDialogCopied(true)
                  toast.success({ title: t("common.copied"), description: t("meetings.meetingLinkCopied") })
                  setTimeout(() => setShareDialogCopied(false), 1600)
                }}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium shrink-0"
              >
                {shareDialogCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {shareDialogCopied ? t("common.copied") : t("common.copy")}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${meeting.title}\n${meetingUrl}`)}`}
                target="_blank" rel="noreferrer"
                className="flex flex-col items-center gap-1 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-xs text-foreground"
              >
                <MessageSquare className="w-5 h-5 text-emerald-500" />
                WhatsApp
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(meeting.title)}&body=${encodeURIComponent(`Join my meeting: ${meetingUrl}`)}`}
                className="flex flex-col items-center gap-1 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-xs text-foreground"
              >
                <Paperclip className="w-5 h-5 text-blue-500" />
                Email
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${meeting.title} — ${meetingUrl}`)}`}
                target="_blank" rel="noreferrer"
                className="flex flex-col items-center gap-1 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors text-xs text-foreground"
              >
                <Share2 className="w-5 h-5 text-sky-500" />
                Twitter
              </a>
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowShareDialog(false)}>
                {t("common.close")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
